import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchOrders, updateUserAddress, updateUserPassword } from '../services/api';
import { useAuth } from '../context/AuthContext';
const ORDER_STATUS_LABELS = {
  pending: 'En attente',
  shipped: 'Commande expédiée'
};
const formatDate = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return value;
  }
};

function EyeIcon({ isHidden }) {
  return isHidden ? (
    <svg viewBox="0 0 24 24" className="password-eye-icon" aria-hidden="true">
      <path
        d="M3 3l18 18"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M10.2 10.2a3.15 3.15 0 0 0 4.47 4.47"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M6.1 6.2C4 7.8 2.8 9.7 2 11.5c1.6 3.8 5.2 6.5 10 6.5 1.5 0 2.9-.2 4.1-.7"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M11.8 6c3.4.2 6.2 2.1 8.2 5.5-.8 1.9-2.2 3.8-4.2 5.1"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ) : (
    <svg viewBox="0 0 24 24" className="password-eye-icon" aria-hidden="true">
      <path
        d="M2 12c2.2-4 5.8-6.5 10-6.5s7.8 2.5 10 6.5c-2.2 4-5.8 6.5-10 6.5S4.2 16 2 12z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      <circle cx="12" cy="12" r="3.5" stroke="currentColor" strokeWidth="1.5" fill="none" />
    </svg>
  );
}

export default function Account() {
  const { user: profile, refreshUser, loading: authLoading } = useAuth();
  const [addressFormOpen, setAddressFormOpen] = useState(false);
  const [passwordFormOpen, setPasswordFormOpen] = useState(false);
  const [addressForm, setAddressForm] = useState({
    street: '',
    postalCode: '',
    city: '',
    country: ''
  });
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    password: '',
    confirmPassword: ''
  });
  const [statusMessage, setStatusMessage] = useState('');
  const [orderHistory, setOrderHistory] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersError, setOrdersError] = useState('');
  const navigate = useNavigate();
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    next: false,
    confirm: false
  });

  useEffect(() => {
    if (profile) {
      setAddressForm({
        street: profile.street || '',
        postalCode: profile.postalCode || '',
        city: profile.city || '',
        country: profile.country || ''
      });
    } else {
      setAddressForm({ street: '', postalCode: '', city: '', country: '' });
    }
    setPasswordForm({ currentPassword: '', password: '', confirmPassword: '' });
  }, [profile]);

  useEffect(() => {
    if (!profile) {
      setOrderHistory([]);
      return;
    }
    const normalizedEmail = (profile.normalizedEmail || profile.email || '').toLowerCase();
    if (!normalizedEmail) return;
    setOrdersLoading(true);
    setOrdersError('');
    fetchOrders()
      .then((orders) => {
        const filtered = orders.filter((order) => {
          const orderEmail = (order.customer?.email || '').toLowerCase();
          return orderEmail === normalizedEmail;
        });
        setOrderHistory(filtered);
      })
      .catch((err) => setOrdersError(err.message))
      .finally(() => setOrdersLoading(false));
  }, [profile]);

  if (authLoading) {
    return (
      <section className="account-page">
        <div className="account-page__card">
          <h1>Mon compte</h1>
          <p>Chargement de vos informations...</p>
        </div>
      </section>
    );
  }

  if (!profile) {
    return (
      <section className="account-page">
        <div className="account-page__card">
          <h1>Mon compte</h1>
          <p>Vous devez être connecté pour consulter vos informations personnelles.</p>
          <button type="button" className="site-header__cta" onClick={() => navigate('/')}>
            Retour à l’accueil
          </button>
        </div>
      </section>
    );
  }

  const handleAddressSubmit = async (event) => {
    event.preventDefault();
    setStatusMessage('');
    if (!profile?._id) {
      setStatusMessage('Utilisateur introuvable.');
      return;
    }
    try {
      await updateUserAddress(profile._id, addressForm);
      await refreshUser();
      setStatusMessage('Adresse postale mise à jour.');
      setAddressFormOpen(false);
    } catch (error) {
      setStatusMessage(error.message || "Impossible de mettre à jour l'adresse pour le moment.");
    }
  };

  const handlePasswordSubmit = async (event) => {
    event.preventDefault();
    setStatusMessage('');
    if (!passwordForm.password) {
      setStatusMessage('Veuillez renseigner un nouveau mot de passe.');
      return;
    }
    if (passwordForm.password.length < 6 || !/[!@#$%^&*(),.?":{}|<>+=-]/.test(passwordForm.password) || !/\d/.test(passwordForm.password)) {
      setStatusMessage('Votre mot de passe doit contenir au minimum 6 caractères, 1 chiffre et 1 caractère spécial.');
      return;
    }
    if (passwordForm.password !== passwordForm.confirmPassword) {
      setStatusMessage('Les mots de passe doivent être identiques.');
      return;
    }
    if (!profile?._id) {
      setStatusMessage('Utilisateur introuvable.');
      return;
    }
    try {
      await updateUserPassword(profile._id, {
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.password
      });
      setPasswordForm({ currentPassword: '', password: '', confirmPassword: '' });
      setStatusMessage('Votre mot de passe est mis à jour.');
      setPasswordFormOpen(false);
    } catch (error) {
      setStatusMessage(error.message || 'Impossible de modifier le mot de passe pour le moment.');
    }
  };

  return (
    <section className="account-page">
      <div className="account-page__card">
        <h1>Mon compte</h1>
        <div className="account-page__details">
          <h2>Informations personnelles</h2>
          <p>
            <strong>Nom :</strong> {profile.civility ? `${profile.civility} ` : ''}
            {profile.firstName} {profile.lastName}
          </p>
          <p>
            <strong>Email :</strong> {profile.email}
          </p>
          <p>
            <strong>Téléphone :</strong> {profile.phone || 'Non renseigné'}
          </p>
          <p>
            <strong>Adresse :</strong>{' '}
            {profile.street || profile.postalCode || profile.city || profile.country
              ? `${profile.street || ''} ${profile.postalCode || ''} ${profile.city || ''} ${profile.country || ''}`
              : 'Non renseignée'}
          </p>
        </div>
        <div className="account-page__cta">
          <button type="button" className="site-header__cta site-header__cta--ghost" onClick={() => setAddressFormOpen((prev) => !prev)}>
            Modifier mon adresse postale
          </button>
          <button type="button" className="site-header__cta" onClick={() => setPasswordFormOpen((prev) => !prev)}>
            Modifier le mot de passe
          </button>
        </div>

        {addressFormOpen && (
          <form className="account-page__form" onSubmit={handleAddressSubmit}>
            <h2>Adresse postale</h2>
            <label>
              Nom et numéro de la voie
              <input
                type="text"
                value={addressForm.street}
                onChange={(event) => setAddressForm((prev) => ({ ...prev, street: event.target.value }))}
              />
            </label>
            <label>
              Code postal
              <input
                type="text"
                value={addressForm.postalCode}
                onChange={(event) => setAddressForm((prev) => ({ ...prev, postalCode: event.target.value }))}
              />
            </label>
            <label>
              Ville
              <input
                type="text"
                value={addressForm.city}
                onChange={(event) => setAddressForm((prev) => ({ ...prev, city: event.target.value }))}
              />
            </label>
            <label>
              Pays
              <input
                type="text"
                value={addressForm.country}
                onChange={(event) => setAddressForm((prev) => ({ ...prev, country: event.target.value }))}
              />
            </label>
            <div className="account-edit-actions">
              <button type="submit" className="site-header__cta">
                Modifier
              </button>
              <button type="button" className="site-header__cta site-header__cta--ghost" onClick={() => setAddressFormOpen(false)}>
                Annuler
              </button>
            </div>
          </form>
        )}

        {passwordFormOpen && (
          <form className="account-page__form" onSubmit={handlePasswordSubmit}>
            <h2>Mot de passe</h2>
            <label>
              Ancien mot de passe
              <div className="password-input">
                <input
                  type={showPasswords.current ? 'text' : 'password'}
                  value={passwordForm.currentPassword}
                  onChange={(event) =>
                    setPasswordForm((prev) => ({ ...prev, currentPassword: event.target.value }))
                  }
                />
                <button
                  type="button"
                  className="password-input__toggle"
                  onClick={() =>
                    setShowPasswords((prev) => ({ ...prev, current: !prev.current }))
                  }
                  aria-label={showPasswords.current ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                >
                  <EyeIcon isHidden={!showPasswords.current} />
                </button>
              </div>
            </label>
            <label>
              Nouveau mot de passe
              <div className="password-input">
                <input
                  type={showPasswords.next ? 'text' : 'password'}
                  value={passwordForm.password}
                  onChange={(event) => setPasswordForm((prev) => ({ ...prev, password: event.target.value }))}
                />
                <button
                  type="button"
                  className="password-input__toggle"
                  onClick={() =>
                    setShowPasswords((prev) => ({ ...prev, next: !prev.next }))
                  }
                  aria-label={showPasswords.next ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                >
                  <EyeIcon isHidden={!showPasswords.next} />
                </button>
              </div>
            </label>
            <label>
              Confirmer le mot de passe
              <div className="password-input">
                <input
                  type={showPasswords.confirm ? 'text' : 'password'}
                  value={passwordForm.confirmPassword}
                  onChange={(event) =>
                    setPasswordForm((prev) => ({ ...prev, confirmPassword: event.target.value }))
                  }
                />
                <button
                  type="button"
                  className="password-input__toggle"
                  onClick={() =>
                    setShowPasswords((prev) => ({ ...prev, confirm: !prev.confirm }))
                  }
                  aria-label={showPasswords.confirm ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                >
                  <EyeIcon isHidden={!showPasswords.confirm} />
                </button>
              </div>
            </label>
            <div className="account-edit-actions">
              <button type="submit" className="site-header__cta">
                Modifier
              </button>
              <button type="button" className="site-header__cta site-header__cta--ghost" onClick={() => setPasswordFormOpen(false)}>
                Annuler
              </button>
            </div>
          </form>
        )}
        <div className="account-page__orders">
          <h2>Historique des commandes</h2>
          {ordersLoading ? (
            <p>Chargement des commandes...</p>
          ) : ordersError ? (
            <p className="account-page__status">{ordersError}</p>
          ) : orderHistory.length ? (
          <div className="account-orders-table-wrapper">
            <table className="account-orders-table">
              <thead>
                <tr>
                  <th>Commande</th>
                  <th>Date</th>
                  <th>Montant</th>
                  <th>Statut</th>
                  <th>Articles</th>
                </tr>
              </thead>
              <tbody>
                {orderHistory.map((order) => (
                  <tr key={order._id}>
                    <td>#{String(order._id).slice(-6)}</td>
                    <td>{formatDate(order.createdAt)}</td>
                    <td>{Number(order.total).toFixed(2)} €</td>
                    <td>{ORDER_STATUS_LABELS[order.status] || order.status}</td>
                    <td>
                      <ul className="account-order-items">
                        {order.items?.map((item, index) => (
                          <li key={`${order._id}-${index}`}>
                            {item.name} ({item.size || 'unique'}) × {item.quantity}
                          </li>
                        ))}
                      </ul>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          ) : (
            <p>Vous n’avez pas encore passé de commande.</p>
          )}
        </div>
        {statusMessage && <p className="account-page__status">{statusMessage}</p>}
      </div>
    </section>
  );
}
